import React from "react";
import {useTranslation} from "react-i18next";

import {ContainerValue} from "components/containers";
import {Button, TitleH2, TitleH3, TitleH4} from "components/generic";
import {useRange} from "components/Range";
import {DateLine, SocialPreferences, SpokenLanguages} from "pages/ProfileUser/SimpleComponents";

import {AboutStudent, AboutStudentList, FlexContainer, ImageTextContainer, SocialPreference,} from "./styles";
import {IUserShot} from "interfaces/intarfaces";
import {getNameCountry} from "utils/fetchMapInfo";
import {useContextHospi} from "context/ContextHospi";
import {TLang} from "assets/data/isoNameLanguage";
import {getDateOrNone, isoDate} from "utils/helpers";
import {useCalendar} from "components/Calendar";

export const activeYears = (years: number) =>
  Math.round(years ?? 0) < 1 ? "1" : Number(years).toFixed(1);

export const StudentAboutInfo: React.FC<
  Partial<IUserShot> & {
    closeBtn: (x: number) => void;
    isPreviewMode?: boolean
  }
> = ({
  thumbnail_url,
  first_name,
  last_name,
  gender,
  birth_date,
  birth_country,
  spoken_languages,
  description,
  privacy,
  autonomy,
  requested_autonomy,
  active_years,
  house_rents_count,
  hosted_tenants_count,
  preferred_move_in,
  preferred_move_out,
  closeBtn,
  isPreviewMode = false
}) => {
    const { t, i18n } = useTranslation("matches");
    const { userStatus } = useContextHospi();

    const privacy_var = useRange(privacy);
    const autonomy_var = useRange(autonomy ?? requested_autonomy);

    const name = `${first_name} ${last_name}`;
    const age =
      new Date().getFullYear() - new Date(birth_date ?? new Date()).getFullYear();

    const countTimes = house_rents_count ?? hosted_tenants_count ?? 0;

    const moveInDate = useCalendar({
      value: getDateOrNone(preferred_move_in),
    });
    const moveOutDate = useCalendar({
      value: getDateOrNone(preferred_move_out),
    });

    if (!first_name && !last_name)
      return (
        <div>
          <TitleH2 style={{ padding: "24px 0 0 24px" }}>Warning</TitleH2>
          <TitleH3 style={{ margin: "24px 24px 0" }}>
            Something went wrong, 103
          </TitleH3>
          <Button
            main
            onClick={() => closeBtn(0)}
            style={{ float: "right", margin: "24px", fontSize: "18px" }}
          >
            {t(`${userStatus}.userInfo.close`)}
          </Button>
        </div>
      );



    const currLang = i18n.language.toUpperCase() as TLang;
    return (
      <>
        <TitleH2>{t(`${isPreviewMode ? 'host' : userStatus}.userInfo.title`)}</TitleH2>
        <FlexContainer>
          <AboutStudent>
            <ContainerValue>
              <TitleH2>{t(`${isPreviewMode ? 'student' : userStatus}.userInfo.about`)}</TitleH2>
              <ImageTextContainer>
                <img src={thumbnail_url + `?${new Date().toString()}`} alt="Student" />
                <div className="textContainer">
                  <TitleH3>{name}</TitleH3>
                  <TitleH4>
                    <strong>{activeYears(active_years ?? 0) === "1" &&
                      t(`${isPreviewMode ? 'host' : userStatus}.userInfo.LessThan`) + " " + activeYears(active_years ?? 0)}</strong>{" "}
                    {t(`${isPreviewMode ? 'host' : userStatus}.userInfo.yearsActive`)}
                  </TitleH4>
                  <TitleH4>
                    {countTimes === 0 &&
                      t(`${isPreviewMode ? 'host' : userStatus}.userInfo.roomsRented.0`)}
                    {countTimes > 0 && (
                      <>
                        {" "}
                        <strong>{countTimes}</strong> {t(`${userStatus}.userInfo.roomsRented.${countTimes === 1 ? 1 : 2}`)}

                      </>
                    )}
                  </TitleH4>
                </div>
              </ImageTextContainer>
              <AboutStudentList>
                <li key="gender">{t(`${userStatus}.userInfo.gender.${gender}`)}</li>
                {(userStatus === "host" || isPreviewMode) && (
                  <>
                    <li key="age">
                      {age} {t("host.card.yearOld")}
                    </li>
                    <li key="country">{getNameCountry(birth_country || "", currLang)}</li>
                  </>
                )}
              </AboutStudentList>
              {spoken_languages && spoken_languages.length > 0 && (
                <SpokenLanguages items={spoken_languages || ""} />
              )}
              <TitleH3>{t(`${userStatus}.userInfo.preferredDate`)}</TitleH3>
              <div className="dates">
                <DateLine
                  start={isoDate(moveInDate.value)}
                  end={isoDate(moveOutDate.value)}
                  ico
                />
              </div>
              <TitleH3>{t(`${isPreviewMode ? 'student' : userStatus}.userInfo.description`)}</TitleH3>
              <div className="description">{description}</div>

            </ContainerValue>
          </AboutStudent>
          <SocialPreference>
            <ContainerValue>
              <TitleH2 m="0 0 20px 0">
                {t(`${isPreviewMode ? 'host' : userStatus}.userInfo.preference`)}
              </TitleH2>
              <SocialPreferences
                roleUser={isPreviewMode ? 'student' : userStatus === "student" ? "host" : "student"}
                privacy={privacy_var}
                newInTown={autonomy_var}
              />
            </ContainerValue>
            <Button
              main
              onClick={() => closeBtn(0)}
              style={{ float: "right", padding: "8px 40px", fontSize: "18px" }}
            >
              {t(`${userStatus}.userInfo.close`)}
            </Button>
          </SocialPreference>
        </FlexContainer>
      </>
    );
  };
